import { getManagementByEntityType, getTemplateByHost } from '@/api/apiDocV3'

export const DEFAULT_BRAND = 'AWC'
export const DEFAULT_FAVICON = '/img/awc_favicon.ico'

export const shouldShowDevHostSelect = (isDev, nodeEnv) => (
  !!isDev
  || process.env.NODE_ENV === 'development'
  || nodeEnv !== 'production'
)

const LEGACY_HOST_STORAGE_KEY = 'apiDocV3Host'
const HOST_STORAGE_KEY = 'devHost'

/** brandText → 允許的 route name（非 template 欄位，仍由前端設定） */
export const BRAND_PAGES = {
  'SANA TECH': [
    'SingleWalletTesting',
    'ApiDocV2',
    'ApiDocV3',
    'PublicPage',
    'MaintBoard',
  ],
  IN2SOLUTIONS: [
    'SingleWalletTesting',
    'ApiDocV2',
    'ApiDocV3',
    'PublicPage',
    'MaintBoard',
  ],
}

export const domainToHost = domain => (domain || '').replace(/^https?:\/\//, '').replace(/\/$/, '')

let templates = []
/** DevHostSelect 是否已載入完整 template list */
let templatesListLoaded = false

let resolvedBrand = {
  brandText: DEFAULT_BRAND,
  faviconUrl: DEFAULT_FAVICON,
  host: '',
  template: null,
}

export const getTemplates = () => templates

export const getResolvedBrand = () => resolvedBrand

export const getCurrentDevOrWindowHost = () => (
  localStorage.getItem(HOST_STORAGE_KEY)
  || localStorage.getItem(LEGACY_HOST_STORAGE_KEY)
  || window.location.host
)

/** template 尚未載入或找不到對應 domain 時，依 hostname 後備（含舊版靜態 favicon） */
const hostnameHeuristicBrand = (host) => {
  if ((host || '').includes('sanatechinfo.com')) {
    return {
      brandText: 'SANA TECH',
      faviconUrl: '/img/sanatech_favicon.png',
      host,
      template: null,
    }
  }
  if ((host || '').includes('in2info.cc')) {
    return {
      brandText: 'IN2SOLUTIONS',
      faviconUrl: '/img/in2_favicon.ico',
      host,
      template: null,
    }
  }
  return {
    brandText: DEFAULT_BRAND,
    faviconUrl: DEFAULT_FAVICON,
    host,
    template: null,
  }
}

const brandFromTemplate = (template, host) => ({
  brandText: template?.brandText || DEFAULT_BRAND,
  faviconUrl: template?.faviconUrl || DEFAULT_FAVICON,
  host,
  template: template || null,
})

export const resolveBrandByHost = (hostInput) => {
  const host = domainToHost(hostInput) || hostInput || window.location.host
  const matched = templates.find(t => t.isPrimary && domainToHost(t.domain) === host)
  if (matched) return brandFromTemplate(matched, host)
  return hostnameHeuristicBrand(host)
}

export const setResolvedBrandByHost = (hostInput) => {
  resolvedBrand = resolveBrandByHost(hostInput)
  return resolvedBrand
}

export const applyFavicon = (faviconUrl) => {
  const href = faviconUrl || DEFAULT_FAVICON
  let link = document.querySelector('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = href
}

/**
 * 生產／一般啟動：只依目前 host 向後端取對應 template（單一筆）
 * 失敗時用 hostname 後備
 */
export const preloadBrandFromTemplates = async () => {
  // 生產對齊實際瀏覽 host；開發環境才沿用 DevHost 選擇
  const host = process.env.NODE_ENV === 'development'
    ? getCurrentDevOrWindowHost()
    : window.location.host
  try {
    const { data } = await getTemplateByHost(host)
    if (data && (data.id || data.domain)) {
      // 先塞入 cache，讓後續 setResolvedBrandByHost／beforeEach 能對到同一筆
      if (!templatesListLoaded) templates = [data]
      resolvedBrand = brandFromTemplate(data, domainToHost(data.domain) || host)
    } else {
      if (!templatesListLoaded) templates = []
      resolvedBrand = hostnameHeuristicBrand(host)
    }
  } catch (error) {
    console.error('[brandFromTemplates] preload by-host failed', error)
    if (!templatesListLoaded) templates = []
    resolvedBrand = hostnameHeuristicBrand(host)
  }
  applyFavicon(resolvedBrand.faviconUrl)
  return resolvedBrand
}

/** DevHostSelect：載入全部 template 供 Host 下拉 */
export const ensureTemplatesListLoaded = async () => {
  if (templatesListLoaded && templates.length) return templates
  try {
    const { data } = await getManagementByEntityType('template')
    templates = Array.isArray(data) ? data : []
    templatesListLoaded = true
  } catch (error) {
    console.error('[brandFromTemplates] load template list failed', error)
    templates = []
    templatesListLoaded = false
  }
  resolvedBrand = resolveBrandByHost(getCurrentDevOrWindowHost())
  applyFavicon(resolvedBrand.faviconUrl)
  return templates
}

/** host → select option + brand meta（供 DevHostSelect；僅 isPrimary template） */
export const buildHostOptionsFromTemplates = () => {
  const hostMap = new Map()
  const metaByHost = {}
  templates.forEach((template) => {
    if (!template.isPrimary) return

    const {
      templateName, domain, brandText, faviconUrl, swaPlatforms,
    } = template

    const formattedHost = domainToHost(domain)
    if (!formattedHost || hostMap.has(formattedHost)) return

    hostMap.set(formattedHost, {
      label: templateName ? `${templateName} - ${formattedHost}` : formattedHost,
      value: formattedHost,
    })

    metaByHost[formattedHost] = {
      brandText: brandText || '',
      templateName: templateName || '',
      domain: domain || '',
      faviconUrl: faviconUrl || DEFAULT_FAVICON,
      swaPlatforms: Array.isArray(swaPlatforms) ? swaPlatforms : [],
    }
  })

  return {
    hostOptions: [...hostMap.values()].sort((a, b) => a.label.localeCompare(b.label)),
    metaByHost,
  }
}
